import React, { useCallback, useState } from "react";
import themeGet from "@styled-system/theme-get";
import {
  Button,
  Flex,
  H5,
  Box,
  Divider,
  Icon,
  Modal,
  P,
  Small,
  TextInput,
} from "orcs-design-system";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import moment from "moment";
import useAIDashboards from "src/hooks/useAIDashboards";
import AIIcon from "src/components/AIContent/AI_Icon";
import ErrorNotification from "src/components/ErrorNotification";
import Url, { url } from "src/consts/urlPaths";
import { resolveAiDashboardPath } from "src/util/aiDashboardPath";

/** Takes the row's spare width and ellipsises rather than wrapping. */
const rowTitle = css`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DashboardLink = styled(NavLink)`
  ${rowTitle}
  text-decoration: none !important;
  font-size: ${themeGet("fontSizes.1")};
  color: ${themeGet("colors.bodyText")};

  &.active,
  &:hover {
    color: ${themeGet("colors.bodyText")};
  }
`;

/** Muted stand-in for the link when a v1 dashboard has no chat to link to. */
const OrphanTitle = styled(Box)`
  ${rowTitle}
  color: ${themeGet("colors.mutedText")};
`;

/** nowrap so the age can't fold onto two lines as the row makes room for it. */
const RowAge = styled(Small)`
  font-size: 0.75rem;
  color: ${themeGet("colors.mutedText")};
  white-space: nowrap;
`;

/** Row wrapper so the action buttons sit beside the link rather than inside it. */
const DashboardRow = styled(Flex)`
  padding: ${themeGet("space.s")};
  border-radius: ${themeGet("radii.2")};
  transition: ${themeGet("transition.transitionDefault")};
  align-items: center;
  justify-content: space-between;
  gap: ${themeGet("space.xs")};

  &:has(a.active) {
    background-color: ${themeGet("colors.navActiveBackground")};
  }

  &:hover {
    background-color: ${themeGet("colors.hoverSurface")};
  }

  /* Panel rows are narrow, so the age and the actions collapse to nothing until
     the row is hovered and the title gets the whole row. Collapsing the width
     rather than fading it out is what actually hands the space back — an
     opacity-only hide still reserves it. They stay in flow and focusable, so
     focus-within reveals them for keyboard users too. */
  [data-dashboard-row-actions] {
    width: 0;
    overflow: hidden;
  }

  &:hover [data-dashboard-row-actions],
  &:focus-within [data-dashboard-row-actions] {
    width: auto;
  }
`;

// The action buttons are siblings of the row's NavLink rather than children of
// it, so a click on one cannot reach the link and no preventDefault is needed
// to stop the row navigating. The tests assert the location stays put.

const AIDashboardsContent = () => {
  const { dashboards, loading, renameDashboard, hideDashboard } =
    useAIDashboards();
  const history = useHistory();
  const location = useLocation();

  const [editingId, setEditingId] = useState(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const cancelRename = useCallback(() => {
    setEditingId(null);
    // Blanking the draft also disarms the blur that follows an Escape.
    setDraftTitle("");
  }, []);

  const startRename = useCallback((dashboard) => {
    setEditingId(dashboard.id);
    setDraftTitle(dashboard.title);
  }, []);

  const saveRename = useCallback(
    async (dashboard) => {
      const title = draftTitle.trim();
      cancelRename();

      if (!title || title === dashboard.title) {
        return;
      }

      try {
        await renameDashboard(dashboard.id, title);
      } catch (renameError) {
        setError(renameError);
      }
    },
    [draftTitle, cancelRename, renameDashboard]
  );

  const closeDeleteModal = useCallback(() => setDashboardToDelete(null), []);

  const confirmDelete = useCallback(async () => {
    const dashboard = dashboardToDelete;
    setIsDeleting(true);

    try {
      await hideDashboard(dashboard.id);
    } catch (deleteError) {
      // The dashboard is still there, so stay where we are and surface why.
      setError(deleteError);
      return;
    } finally {
      setIsDeleting(false);
      setDashboardToDelete(null);
    }

    // Both dashboard routes end in the dashboard id, so leave the page we just
    // deleted instead of letting it fail its next fetch.
    if (location.pathname.endsWith(`/${dashboard.id}`)) {
      history.push(url(Url.APP_HOME));
    }
  }, [dashboardToDelete, hideDashboard, location.pathname, history]);

  if (loading) {
    return (
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
        p="r"
      >
        <Small color="greyMedium">Loading dashboards...</Small>
      </Flex>
    );
  }

  if (!dashboards || dashboards.length === 0) {
    return (
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
        p="r"
      >
        <Box mb="s">
          <AIIcon width={48} height={48} />
        </Box>
        <Small color="greyMedium" textAlign="center">
          No AI-generated dashboards yet. Ask the AI to create a dashboard in a
          chat!
        </Small>
      </Flex>
    );
  }

  return (
    // Block-level, not a flex container: the panel's close button is floated
    // right, and a flex container refuses to overlap a float — which would
    // narrow every row by the width of that button.
    <>
      <Box mb="r">
        <Flex alignItems="center" gap="xs">
          <AIIcon width={20} height={20} />

          <H5 fontWeight="bold" data-testid="heading">
            Dashboards
          </H5>
        </Flex>

        <Box my="r">
          <Divider light />
        </Box>
        <Flex flexDirection="column" gap="xs">
          {dashboards.map((dashboard) => {
            const { id, title, aiChatId, createdAt, format } = dashboard;

            if (editingId === id) {
              return (
                <DashboardRow key={id}>
                  <TextInput
                    autoFocus
                    fullWidth
                    height="28px"
                    id={`ai-dashboard-title-${id}`}
                    ariaLabel="Dashboard name"
                    value={draftTitle}
                    data-testid={`ai-dashboard-title-input-${id}`}
                    onChange={(event) => setDraftTitle(event.target.value)}
                    onBlur={() => saveRename(dashboard)}
                    onKeyDown={(event) => {
                      // Let the blur handler own the save so Enter can't
                      // submit the rename twice.
                      if (event.key === "Enter") {
                        event.target.blur();
                      }
                      if (event.key === "Escape") {
                        cancelRename();
                      }
                    }}
                  />
                </DashboardRow>
              );
            }

            const dashboardLink = resolveAiDashboardPath({
              dashboardId: id,
              chatId: aiChatId,
              format,
            });

            return (
              <DashboardRow key={id}>
                {dashboardLink ? (
                  <DashboardLink to={dashboardLink} title={title}>
                    {title}
                  </DashboardLink>
                ) : (
                  // v1 without aiChatId — no legacy route; don't send users to v2.
                  <OrphanTitle title="Dashboard is missing its chat link">
                    {title}
                  </OrphanTitle>
                )}

                <Flex
                  alignItems="center"
                  gap="xs"
                  flex="0 0 auto"
                  data-dashboard-row-actions
                >
                  <RowAge>{moment(createdAt).fromNow()}</RowAge>
                  <Button
                    type="button"
                    variant="ghost"
                    small
                    iconOnly
                    ariaLabel={`Rename dashboard ${title}`}
                    onClick={() => startRename(dashboard)}
                    data-testid={`ai-dashboard-rename-${id}`}
                  >
                    <Icon icon={["fas", "pencil"]} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    small
                    iconOnly
                    ariaLabel={`Delete dashboard ${title}`}
                    onClick={() => setDashboardToDelete(dashboard)}
                    data-testid={`ai-dashboard-delete-${id}`}
                  >
                    <Icon icon={["far", "trash-alt"]} />
                  </Button>
                </Flex>
              </DashboardRow>
            );
          })}
        </Flex>
      </Box>

      {dashboardToDelete && (
        <Modal
          visible
          onClose={closeDeleteModal}
          width={["90vw", "90vw", "60vw", "40vw"]}
          headerContent={<H5 fontWeight="bold">Delete dashboard</H5>}
          footerContent={
            <Flex gap="s">
              <Button
                variant="danger"
                onClick={confirmDelete}
                disabled={isDeleting}
                isLoading={isDeleting}
                data-testid="ai-dashboard-delete-confirm"
                px="l"
              >
                Delete
              </Button>
              <Button
                variant="ghost"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </Flex>
          }
        >
          <P my="r">
            {`Remove "${dashboardToDelete.title}" from your dashboards? You won't be able to open it again.`}
          </P>
        </Modal>
      )}

      {error && (
        <ErrorNotification
          message={error.message}
          error={error}
          onDismiss={() => setError(null)}
        />
      )}
    </>
  );
};

export default AIDashboardsContent;
